import React from 'react';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import DateInput from 'src/components/UI/input/DateInput';
import SelectInput from 'src/components/UI/input/SelectInput';
import MultipleSelectInput from 'src/components/UI/input/MultipleSelectInput';
import AutocompleteInput from 'src/components/UI/input/AutocompleteInput';
import InfoTooltip from 'src/components/UI/addons/InfoTooltip';
import SimpleAutocompleteInput from './SimpleAutocompleteInput';
import { ThemeContext } from 'src/store/ContextStore';
const REQ_CLASS = 'required-field';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Global styles using styled components approach
const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== 'borderColor'
})(({ theme: { userTheme: theme }, borderColor }) => ({
  position: 'relative',
  '& .MuiOutlinedInput-root': {
    ...(borderColor && {
      border: `1px solid ${borderColor}`
    }),
    paddingLeft: '8px',
    backgroundColor:
      theme?.appearance?.value !== 'light'
        ? theme?.appearance?.shade
        : '#ffffff'
  },
  '& .MuiOutlinedInput-input': {
    backgroundColor: 'transparent',
    color: theme ? theme.appearance?.text : '#000'
  },
  '& .MuiInputBase-root.Mui-disabled': {
    color: '#000'
  },
  '& .MuiInputBase-input.Mui-disabled': {
    backgroundColor: theme
      ? theme.appearance?.value !== 'light' && theme.appearance?.shade
      : '#fff',
    cursor: 'not-allowed'
  },
  '& input': {
    textOverflow: 'ellipsis'
  },
  '& .Mui-error': {
    marginLeft: 0
  },

  [`&.${REQ_CLASS} .MuiOutlinedInput-notchedOutline`]: {
    borderLeft: '3px solid red'
  },

  [`&.${REQ_CLASS} .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline,
    &.${REQ_CLASS} .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline`]:
    {
      borderLeft: '3px solid red'
    }
}));

const RedLeftBorder = styled('div')({
  '& .MuiOutlinedInput-notchedOutline, \
   & fieldset': {
    borderLeft: '3px solid red'
  },
  // hover & focus
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, \
   & .MuiOutlinedInput-root:hover fieldset, \
   & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, \
   & .MuiOutlinedInput-root.Mui-focused fieldset': {
    borderLeft: '3px solid red'
  }
});

const LabelContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 5
});

const TextInput = React.forwardRef(function TextInput(props, ref) {
  // props specific to this component only
  const {
    label,
    type,
    // updateParent,
    updateParentOnBlur,
    validationFn,
    infoLabel,
    id,
    inputProps = {},
    // Legacy composed props are removed in MUI v9. Pull `InputProps` out of
    // `...rest` (otherwise it reaches the real TextField as a deprecated prop)
    // and accept a modern `slotProps` too; both are folded into the internal
    // TextField's `slotProps` below.
    InputProps,
    slotProps: slotPropsProp,
    borderColor = '',
    onBlur,
    onDirty,
    // Custom (non-MUI) props consumed via `props.*` below — pull them out of
    // `...rest` so React 19 doesn't warn about unknown DOM attributes on the
    // input. NOTE: `helperText` is a real MUI TextField prop (rendered as the
    // caption, never forwarded to the DOM) so it MUST stay in `...rest`.
    helpText: _helpText,
    captionText: _captionText,
    helperTextColour: _helperTextColour,
    ...rest
  } = props;

  const { theme } = React.useContext(ThemeContext);
  const { name, value, onChange, disabled } = props;

  // Associate the (separately-rendered) FormLabel with the input for a11y.
  // Use a caller-supplied id when present, else a stable generated one.
  const reactId = React.useId();
  const inputId = id || reactId;

  // TODO try using debounce
  const [inputValue, setInputValue] = React.useState(value);

  React.useEffect(() => {
    if (updateParentOnBlur) setInputValue(value);
  }, [updateParentOnBlur, value]);

  const handleChange = React.useCallback(
    (e) => {
      onDirty?.(e);
      if (updateParentOnBlur) {
        if (!validationFn) {
          return setInputValue(e.target.value);
        }
        if (validationFn(e.target.value)) setInputValue(e.target.value);
      }
      return onChange(e);
    },
    [onChange, onDirty, updateParentOnBlur, validationFn]
  );

  const computedOnBlur =
    onBlur ??
    (updateParentOnBlur && inputValue !== value ? onChange : undefined);

  // v9-clean slot forwarding: `InputProps` -> slotProps.input (all variants,
  // preserving the pre-migration `...rest` behaviour) and `inputProps` ->
  // slotProps.htmlInput. Empty slots are harmless no-ops.
  const mergedSlotProps = {
    ...slotPropsProp,
    input: { ...(InputProps || {}), ...(slotPropsProp?.input || {}) },
    htmlInput: { ...inputProps, ...(slotPropsProp?.htmlInput || {}) }
  };

  return (
    <Grid
      container
      spacing={1}
      sx={{
        justifyContent: 'flex-start',
        alignItems: 'flex-start'
      }}
    >
      {label && (
        <Grid size={12}>
          <LabelContainer>
            <FormLabel required={props.required} htmlFor={inputId}>
              {label}
            </FormLabel>
            {infoLabel && <InfoTooltip label={infoLabel} />}
          </LabelContainer>
        </Grid>
      )}
      {props.helpText && <Grid size={12}>{props.helpText}</Grid>}
      {(!type || type === 'text' || type === 'email') && (
        <Grid size={12}>
          <StyledTextField
            {...rest}
            id={inputId}
            className={props.required ? REQ_CLASS : undefined}
            borderColor={borderColor}
            value={updateParentOnBlur ? inputValue : value}
            onChange={handleChange}
            onBlur={computedOnBlur}
            variant="outlined"
            fullWidth
            validated={undefined}
            inputRef={ref}
            slotProps={mergedSlotProps}
          />
        </Grid>
      )}
      {type === 'number' && (
        <Grid size={12}>
          <StyledTextField
            {...rest}
            id={inputId}
            className={props.required ? REQ_CLASS : undefined}
            type="number"
            value={updateParentOnBlur ? inputValue : value}
            onChange={handleChange}
            onBlur={computedOnBlur}
            variant="outlined"
            fullWidth
            validated={undefined}
            inputRef={ref}
            slotProps={mergedSlotProps}
          />
        </Grid>
      )}
      {type === 'password' && (
        <Grid size={12}>
          <StyledTextField
            {...rest}
            id={inputId}
            className={props.required ? REQ_CLASS : undefined}
            type="password"
            value={updateParentOnBlur ? inputValue : value}
            onChange={handleChange}
            onBlur={computedOnBlur}
            variant="outlined"
            fullWidth
            validated={undefined}
            inputRef={ref}
            slotProps={mergedSlotProps}
          />
        </Grid>
      )}
      {type === 'date' && (
        <Grid size={12}>
          {props.required ? (
            <RedLeftBorder>
              <DateInput
                {...rest}
                fullWidth
                className={REQ_CLASS}
                variant="outlined"
                showPlaceholder
                helperText={props.captionText || props.helperText}
                error={props.error}
                liveText={props.liveText}
                helperTextColour={props.helperTextColour}
              />
            </RedLeftBorder>
          ) : (
            <DateInput
              {...rest}
              fullWidth
              variant="outlined"
              showPlaceholder
              helperText={props.captionText || props.helperText}
              error={props.error}
              liveText={props.liveText}
              helperTextColour={props.helperTextColour}
            />
          )}
        </Grid>
      )}
      {type === 'select' && (
        <Grid size={12}>
          <SelectInput
            {...rest}
            className={props.required ? REQ_CLASS : undefined}
            variant="outlined"
            value={value || ''}
            sx={{
              '& .MuiInputBase-root.Mui-disabled': {
                color: '#000'
              },
              '& .MuiInputBase-input.Mui-disabled': {
                backgroundColor: theme
                  ? theme.appearance.value !== 'light' && theme.appearance.shade
                  : '#fff',
                cursor: 'not-allowed'
              }
            }}
            fullWidth
            showPlaceholder
            readOnly={props.readOnly}
          />
        </Grid>
      )}
      {type === 'multiple-select' && (
        <Grid size={12}>
          {props.required ? (
            <RedLeftBorder>
              <MultipleSelectInput
                name={name}
                value={value}
                onChange={onChange}
                onNewSelection={props.onNewSelection}
                mapper={props.mapper}
                options={props.options}
                disabled={rest?.disabled}
                explicitSelection={props.explicitSelection}
              />
            </RedLeftBorder>
          ) : (
            <MultipleSelectInput
              name={name}
              value={value}
              onChange={onChange}
              onNewSelection={props.onNewSelection}
              mapper={props.mapper}
              options={props.options}
              disabled={rest?.disabled}
              explicitSelection={props.explicitSelection}
            />
          )}
        </Grid>
      )}
      {type === 'textarea' && (
        <Grid size={12}>
          <StyledTextField
            {...rest}
            className={props.required ? REQ_CLASS : undefined}
            variant="outlined"
            helperText={props.captionText || props.helperText}
            name={name}
            value={updateParentOnBlur ? inputValue : value}
            onChange={handleChange}
            onBlur={computedOnBlur}
            multiline
            rows={props.rows ?? 4}
            disabled={disabled ?? undefined}
            inputRef={ref}
            fullWidth
            id={inputId}
            slotProps={mergedSlotProps}
          />
        </Grid>
      )}
      {type === 'autocomplete' && (
        <Grid size={12}>
          <AutocompleteInput
            {...rest}
            className={props.required ? REQ_CLASS : undefined}
            sx={{
              '& .MuiInputBase-root.Mui-disabled': {
                color: '#000'
              },
              '& .MuiInputBase-input.Mui-disabled': {
                backgroundColor: theme
                  ? theme.appearance.value !== 'light' && theme.appearance.shade
                  : '#fff',
                cursor: 'not-allowed'
              }
            }}
            disableClearable
            forcePopupIcon
            popupIcon={<ArrowDropDownIcon />}
          />
        </Grid>
      )}
      {type === 'simple-autocomplete' && (
        <Grid size={12}>
          <SimpleAutocompleteInput
            {...rest}
            className={props.required ? REQ_CLASS : undefined}
            sx={{
              '& .MuiInputBase-root.Mui-disabled': {
                color: '#000'
              },
              '& .MuiInputBase-input.Mui-disabled': {
                backgroundColor: theme
                  ? theme.appearance.value !== 'light' && theme.appearance.shade
                  : '#fff',
                cursor: 'not-allowed'
              }
            }}
          />
        </Grid>
      )}
    </Grid>
  );
});

export default TextInput;
